import { Trans, useTranslation } from 'react-i18next'
import { useMemo, useState } from 'react'
import {
  Text,
  TextLink,
  Box,
  Columns,
  Column,
  Stack,
  Button,
  IconCheck,
  IconRocket,
  Inline,
  useResponsiveValue,
  Alert
} from '@machobear/design-system'
import { useFormikContext } from 'formik'
import { isUndefined } from 'lodash'
import DeleteSponsorModal from './delete-sponsor-modal'
import InfoBox from '../info-box'
import { FormBox, FormBoxSkeleton } from '..'
import { PackageNameToDurationMap } from '../../form-summary'

interface Props {
  packages: any[]
  isLoading: boolean
  availableCredits: number
  onSelectPackage: (pkg: any) => void
}
const SponsoredListing = ({ packages, isLoading, availableCredits, onSelectPackage }: Props) => {
  const { t } = useTranslation()
  const { values } = useFormikContext<any>()
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const isDesktop = useResponsiveValue()({
    mobile: false,
    tablet: false,
    desktop: true,
    wide: true
  })
  const creditCost = useMemo(() => {
    return PackageNameToDurationMap[values.duration as keyof typeof PackageNameToDurationMap]
  }, [values.duration])

  const creditAlert = useMemo(() => {
    if (isUndefined(creditCost)) {
      return null
    }

    return (
      <InfoBox>
        {availableCredits >= creditCost
          ? t(`${creditCost} days of credit will be automatically used on this listing.`)
          : t('Not enough credits. Payment is required to continue.')}
      </InfoBox>
    )
  }, [availableCredits, creditCost, t])

  const onSelect = (pkg: any) => {
    if (pkg.plan_name === values.duration) {
      setShowConfirmModal(true)
      return
    }
    onSelectPackage(pkg)
  }

  const onConfirmDeleteSponsor = () => {
    setShowConfirmModal(false)
    onSelectPackage(null)
  }

  if (isLoading) {
    return (
      <FormBox label={t('Sponsored Listing')}>
        <FormBoxSkeleton />
      </FormBox>
    )
  }

  return (
    <Box data-testid="SponsoredListing">
      <FormBox
        label={t('Sponsored Listing')}
        description={
          <Trans i18nKey="l.listing.sponsored_listing.form.description" t={t}>
            <Text size="standard">
              Get up to 300% more inquiries and boost your listing to the top of the results.{' '}
              <TextLink
                href="https://liv.rent/landlords/sponsored-listings"
                target="_blank"
                rel="noreferrer"
              >
                Learn more
              </TextLink>
            </Text>
          </Trans>
        }
      >
        <Stack space="small">
          {availableCredits > 0 && (
            <Box>
              <Alert tone="info">
                <Text size="small">
                  {t(
                    `You have ${availableCredits} days of sponsored listing credits available. Use it on this listing!`
                  )}
                </Text>
              </Alert>
            </Box>
          )}
          <Columns space="small" collapseBelow="desktop">
            {packages.map((pkg, idx) => (
              <Column key={idx}>
                <Box
                  background="promoteLight"
                  textAlign="center"
                  padding="xxsmall"
                  position="relative"
                  borderTopLeftRadius="standard"
                  borderTopRightRadius="standard"
                  border={pkg.plan_name === values.duration ? 'borderField' : 'borderNeutralLight'}
                  style={{
                    zIndex: 100,
                    borderBottom: 'none',
                    visibility: pkg.popular ? 'visible' : 'hidden',
                    display: !isDesktop && !pkg.popular ? 'none' : 'block'
                  }}
                >
                  <Text size="xsmall" tone="promote" weight="strong">
                    {t('Most Popular')}
                  </Text>
                </Box>
                <Box
                  border={pkg.plan_name === values.duration ? 'borderField' : 'borderNeutralLight'}
                  borderRadius="standard"
                  borderTopLeftRadius={pkg.popular ? 'none' : 'standard'}
                  borderTopRightRadius={pkg.popular ? 'none' : 'standard'}
                  width="full"
                  padding="medium"
                  position="relative"
                  style={{ marginTop: pkg.popular ? '-1px' : '0' }}
                >
                  {isDesktop ? (
                    <Stack space="small">
                      <Inline space="xxxsmall">
                        <IconRocket tone="critical" />
                        {idx > 0 && <IconRocket tone="critical" />}
                        {idx > 1 && <IconRocket tone="critical" />}
                      </Inline>
                      <Text size="medium" weight="strong">
                        {`${pkg.duration} ${t('days')}`}
                      </Text>
                      <Text size="small">
                        ${pkg.daily_price}/{t('day')}
                      </Text>
                      <Box paddingTop="small">
                        {pkg.plan_name === values.duration ? (
                          <Button
                            variant="soft"
                            tone="neutral"
                            onClick={() => onSelect(pkg)}
                            icon={<IconCheck />}
                          >
                            {t('Selected')}
                          </Button>
                        ) : (
                          <Button variant="ghost" onClick={() => onSelect(pkg)}>
                            {(() => {
                              const cost: number =
                                PackageNameToDurationMap[
                                  pkg.plan_name as keyof typeof PackageNameToDurationMap
                                ]
                              return availableCredits < cost ? t('Select') : t('Use Credits')
                            })()}
                          </Button>
                        )}
                      </Box>
                    </Stack>
                  ) : (
                    <Columns space="gutter">
                      <Column>
                        <Stack space="small">
                          <Inline space="xxxsmall">
                            <IconRocket tone="critical" />
                            {idx > 0 && <IconRocket tone="critical" />}
                            {idx > 1 && <IconRocket tone="critical" />}
                          </Inline>
                          <Text size="medium" weight="strong">
                            {`${pkg.duration} ${t('days')}`}
                          </Text>
                          <Text size="small">
                            ${pkg.daily_price}/{t('day')}
                          </Text>
                        </Stack>
                      </Column>
                      <Column>
                        <Box display="flex" alignItems="center" height="full">
                          {pkg.plan_name === values.duration ? (
                            <Button
                              variant="soft"
                              tone="neutral"
                              onClick={() => onSelect(pkg)}
                              icon={<IconCheck />}
                            >
                              {t('Selected')}
                            </Button>
                          ) : (
                            <Button variant="ghost" onClick={() => onSelect(pkg)}>
                              {(() => {
                                const cost: number =
                                  PackageNameToDurationMap[
                                    pkg.plan_name as keyof typeof PackageNameToDurationMap
                                  ]
                                return availableCredits < cost ? t('Select') : t('Use Credits')
                              })()}
                            </Button>
                          )}
                        </Box>
                      </Column>
                    </Columns>
                  )}
                </Box>
              </Column>
            ))}
          </Columns>
          {creditAlert}
        </Stack>
      </FormBox>
      <DeleteSponsorModal
        visible={showConfirmModal}
        onConfirm={() => onConfirmDeleteSponsor()}
        onClose={() => {
          setShowConfirmModal(false)
        }}
      />
    </Box>
  )
}

export default SponsoredListing
